import React, { useState, useEffect } from 'react';
import {
  Button,
  List,
  Input,
  Popconfirm,
  Dropdown,
  message,
  Empty,
  Divider,
  Drawer,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  StarOutlined,
  StarFilled,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  CommentOutlined,
  EditOutlined as PencilOutlined,
  TeamOutlined,
  CloseOutlined,
  MessageOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useChatStore, Conversation } from '../../../store/chatStore';
import { SystemSettings, QuickLink } from '../../../api/public';
import { getMediaUrl } from '../../../utils/request';
import { MijiaAvatarSvg } from './ChatArea';
import type { MenuProps } from 'antd';
import './ConversationList.css';

interface ConversationListProps {
  collapsed: boolean;
  onToggle: () => void;
  systemSettings?: SystemSettings | null;
  quickLinks?: QuickLink[];
}

const ImageLinkButtons: React.FC<{ quickLinks?: QuickLink[] }> = ({ quickLinks }) => {
  const scrollToTop = () => {
    const wrapper = document.querySelector('.chat-content-wrapper');
    if (wrapper) wrapper.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClick = (link: QuickLink) => {
    scrollToTop();
    if (link.link_url && link.link_url !== '#') {
      if (link.link_url.startsWith('http')) {
        window.open(link.link_url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = link.link_url;
      }
      return;
    }
  };

  if (!quickLinks || quickLinks.length === 0) {
    return (
      <div className="drawer-image-links">
        <Button 
          icon={<CommentOutlined />} 
          onClick={() => scrollToTop()}
          block
          className="drawer-link-btn"
        >
          就聊天
        </Button>
        <Button 
          icon={<PencilOutlined />} 
          onClick={() => window.open('https://micahbj.com/huxingtu/', '_blank')}
          block
          className="drawer-link-btn"
        >
          自己画
        </Button>
        <Button 
          icon={<TeamOutlined />} 
          onClick={() => {}}
          block
          className="drawer-link-btn"
        >
          找人画
        </Button>
      </div>
    );
  }

  return (
    <div className="drawer-image-links">
      {quickLinks.map(link => (
        <Button 
          key={link.id}
          icon={
            link.icon_svg ? (
              <span 
                className="drawer-link-icon-svg" 
                dangerouslySetInnerHTML={{ __html: link.icon_svg }} 
                style={{ width: 16, height: 16, marginRight: 8, display: 'inline-flex', alignItems: 'center' }}
              />
            ) : link.image_url ? (
              <img src={getMediaUrl(link.image_url)} style={{ width: 16, height: 16, marginRight: 8 }} alt="" />
            ) : (
              <CommentOutlined />
            )
          } 
          onClick={() => handleClick(link)}
          block
          className="drawer-link-btn"
        >
          {link.title}
        </Button>
      ))}
    </div>
  );
};

const ConversationList: React.FC<ConversationListProps> = ({ collapsed, onToggle, systemSettings, quickLinks }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 监听 collapsed 状态变化，在移动端控制 Drawer
  useEffect(() => {
    if (isMobile) {
      setDrawerVisible(!collapsed);
    }
  }, [collapsed, isMobile]);
  const {
    conversations,
    currentConversationId,
    createConversation,
    deleteConversation,
    updateConversation,
    setCurrentConversation,
  } = useChatStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  // 过滤对话列表：排除临时对话、已软删除的对话
  const filteredConversations = conversations.filter((conv) => {
    if (conv.isTemporary) return false;
    if (conv.deletedAt) return false;
    return true;
  });

  // 普通对话和收藏对话分开
  const normalConversations = filteredConversations.filter((conv) => !conv.isStarred);
  const starredConversations = filteredConversations.filter((conv) => conv.isStarred);

  const handleCreateConversation = async () => {
    try {
      setCurrentConversation(null);
      // 回到顶部
      const wrapper = document.querySelector('.chat-content-wrapper');
      if (wrapper) wrapper.scrollTo({ top: 0, behavior: 'smooth' });
      
      // 移动端点击后关闭 Drawer
      if (isMobile) {
        setDrawerVisible(false);
        onToggle(); // 同步更新 collapsed 状态
      }
    } catch (error) {
      console.error('Failed to clear current conversation selection:', error);
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    onToggle(); // 同步更新 collapsed 状态
  };

  const handleSelectConversation = (id: string) => {
    setCurrentConversation(id);
    // 桌面端点击后不再自动折叠，仅移动端处理（如果需要的话）
    if (isMobile) {
      setDrawerVisible(false);
      onToggle();
    }
  };

  const handleDeleteConversation = async (id: string) => {
    await deleteConversation(id);
    message.success('已删除对话');
  };

  const handleStarConversation = (id: string, isStarred: boolean) => {
    updateConversation(id, { isStarred: !isStarred });
  };

  const handleRenameStart = (conversation: Conversation) => {
    setEditingId(String(conversation.id));
    setEditingTitle(conversation.title);
  };

  const handleRenameConfirm = (id: string) => {
    if (editingTitle.trim()) {
      updateConversation(id, { title: editingTitle.trim() });
      message.success('已重命名');
    }
    setEditingId(null);
    setEditingTitle('');
  };

  const handleRenameCancel = () => {
    setEditingId(null);
    setEditingTitle('');
  };

  const handleExportConversation = (id: string) => {
    const { exportConversation } = useChatStore.getState();
    exportConversation(id);
    message.success('已导出对话');
  };

  const getConversationMenu = (conversation: Conversation): MenuProps['items'] => [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: '重命名',
      onClick: () => handleRenameStart(conversation),
    },
    {
      key: 'star',
      icon: conversation.isStarred ? <StarOutlined /> : <StarFilled />,
      label: conversation.isStarred ? '取消收藏' : '收藏',
      onClick: () => handleStarConversation(String(conversation.id), conversation.isStarred),
    },
    {
      key: 'export',
      icon: <ExportOutlined />,
      label: '导出',
      onClick: () => handleExportConversation(String(conversation.id)),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '删除',
      danger: true,
      onClick: () => handleDeleteConversation(String(conversation.id)),
    },
  ];

  const handleClearConversations = () => {
    Modal.confirm({
      title: '确认清空所有对话？',
      content: '此操作将清空所有对话记录，且无法撤销。',
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const { batchDeleteConversations, conversations } = useChatStore.getState();
          // 获取所有未删除的对话 ID
          const activeConversationIds = conversations
            .filter(conv => !conv.deletedAt)
            .map(conv => String(conv.id));

          if (activeConversationIds.length > 0) {
            // 调用 store 的批量删除方法（它会处理服务器和本地状态）
            await batchDeleteConversations(activeConversationIds);
            message.success('已清空对话记录');
          } else {
            message.info('暂无对话记录');
          }
        } catch (error) {
          console.error('Failed to clear conversations:', error);
          message.error('清空对话失败');
        }
      },
    });
  };

  // 渲染对话列表内容
  const renderConversationContent = () => (
    <>
      <div className="conversation-list-header-integrated">
        <div className="header-buttons">
          <Button
            type="primary"
            onClick={handleCreateConversation}
            className="new-chat-btn"
          >
            <div className="new-chat-btn-avatar-container">
              {systemSettings?.ai_icon_url ? (
                 <img 
                   src={getMediaUrl(systemSettings.ai_icon_url)} 
                   alt="AI" 
                   className="new-chat-btn-img"
                 />
               ) : (
                 <MijiaAvatarSvg 
                   size={48} 
                   className="new-chat-btn-icon animated-sidebar-ai" 
                   interactive={true} 
                   autoExpression={true} 
                 />
               )
              }
            </div>
            <span className="new-chat-btn-text">向 AI 提 问</span>
          </Button>
          <Button
            icon={<DeleteOutlined />}
            onClick={handleClearConversations}
            className="clear-chat-btn"
            title="清空所有记录"
          />
        </div>
      </div>
      {isMobile && <ImageLinkButtons quickLinks={quickLinks} />}
      
      <div className="conversation-list-content">
        {starredConversations.length > 0 && (
          <>
            <div className="conversation-section-title">
              <StarFilled style={{ color: '#faad14', marginRight: 8 }} />
              收藏
            </div>
            <List
              dataSource={starredConversations}
              renderItem={renderConversationItem}
              className="conversation-list-items"
            />
            {normalConversations.length > 0 && <Divider style={{ margin: '12px 0' }} />}
          </>
        )}

        {normalConversations.length > 0 ? (
          <>
            <div className="conversation-section-title">历史会话</div>
            <List
              dataSource={normalConversations}
              renderItem={renderConversationItem}
              className="conversation-list-items"
            />
          </>
        ) : (
          filteredConversations.length === 0 && (
            <Empty description="暂无对话" style={{ marginTop: 40 }} />
          )
        )}
      </div>
    </>
  );

  const renderConversationItem = (conversation: Conversation) => {
    const isActive = String(conversation.id) === String(currentConversationId);
    const isEditing = String(editingId) === String(conversation.id);

    return (
      <List.Item
        key={conversation.id}
        className={`conversation-item ${isActive ? 'active' : ''}`}
        onClick={() => !isEditing && handleSelectConversation(String(conversation.id))}
        actions={[
          <Dropdown
            menu={{ items: getConversationMenu(conversation) }}
            trigger={['click']}
            key="more"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </Dropdown>,
        ]}
      >
        {isEditing ? (
          <Input
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onPressEnter={() => handleRenameConfirm(String(conversation.id))} 
            onBlur={() => handleRenameConfirm(String(conversation.id))}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <div className="conversation-item-content">
            {conversation.isStarred && (
              <StarFilled className="star-icon" style={{ color: '#faad14' }} />
            )}
            <div className="conversation-title">{conversation.title}</div>
          </div>
        )}
      </List.Item>
    );
  };

  // 移动端使用 Drawer
  if (isMobile) {
    return (
      <>
        {/* 移动端收起状态：完全不显示任何按钮（Task 1） */}
        {collapsed ? null : (
          <Drawer
            title={systemSettings?.site_name || '米迦同学'}
            placement="left"
            onClose={handleDrawerClose}
            open={drawerVisible}
            width={280}
            className="conversation-drawer"
            footer={
              <div className="drawer-footer">
                <Button
                  type="default"
                  icon={<PhoneOutlined />}
                  onClick={() => {
                    const phone = systemSettings?.phone_number || '13012803366';
                    window.location.href = `tel:${phone}`;
                  }}
                  block
                  className="support-btn"
                >
                  技术支持：响钹科技
                </Button>
              </div>
            }
          >
            {renderConversationContent()}
          </Drawer>
        )}
      </>
    );
  }

  // 桌面端保持原有样式
  if (collapsed) {
    return (
      <div className="conversation-list collapsed">
        {/* PC端收起状态现在完全隐藏，不显示任何按钮 */}
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-inner">
        {renderConversationContent()}
      </div>
      <div className="drawer-footer">
        <Button
          type="default"
          icon={<PhoneOutlined />}
          onClick={() => {
            const phone = systemSettings?.phone_number || '13012803366';
            window.location.href = `tel:${phone}`;
          }}
          block
          className="support-btn"
        >
          技术支持：响钹科技
        </Button>
      </div>
    </div>
  );
};

export default ConversationList;
